/**
 * Revenue Overview Section
 * Section 1: ARR, MRR, churn, LTV, pipeline
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface RevenueOverviewProps {
  data?: {
    arr?: {
      actual?: number;
      forecasted?: number;
    };
    mrr?: {
      current?: number;
      growthRate?: number;
      expansion?: number;
      contraction?: number;
    };
    churn?: {
      rate?: number;
      probabilityCurves?: Array<{ date: string; probability: number }>;
    };
    ltv?: {
      projections: Array<{ icpSegment: string; ltv: number }>;
    };
    pipeline?: {
      coverage?: number;
      aiForecasted?: number;
    };
  };
}

export function RevenueOverview({ data }: RevenueOverviewProps) {
  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">ARR (Actual)</CardTitle>
            <CardDescription>Annual Recurring Revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data?.arr?.actual)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">ARR (Forecasted)</CardTitle>
            <CardDescription>AI Forecast</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(data?.arr?.forecasted)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">MRR Growth Rate</CardTitle>
            <CardDescription>Month-over-month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data?.mrr?.growthRate)}</div>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="text-green-600">
                +{formatCurrency(data?.mrr?.expansion)}
              </Badge>
              <Badge variant="outline" className="text-red-600">
                -{formatCurrency(data?.mrr?.contraction)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
            <CardDescription>Monthly churn</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data?.churn?.rate)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>LTV Projections by ICP</CardTitle>
            <CardDescription>Customer Lifetime Value</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data?.ltv?.projections?.map((projection, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm">{projection.icpSegment}</span>
                  <span className="font-semibold">{formatCurrency(projection.ltv)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pipeline Coverage</CardTitle>
            <CardDescription>AI Forecasted Pipeline</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Current Coverage</span>
                <span className="font-semibold">{formatCurrency(data?.pipeline?.coverage)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">AI Forecasted</span>
                <span className="font-semibold">{formatCurrency(data?.pipeline?.aiForecasted)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
